import React, { useEffect, useState } from "react";
import AppHeader from "../../components/AppHeader";
import { buildHierarchicalList } from "../../components/staff/constants";
import { fetchStaffMovement, buildStaffMovementExcelUrl } from "../../services/webReportsService";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { myanmarFontBase64 } from "../../utils/myanmarFontBase64";

// Badge styling helper
const getBadgeStyles = (eventType) => {
  switch (eventType) {
    case "JOINING":
    case "REHIRE":
      return { backgroundColor: "#d1fae5", color: "#065f46" }; // Emerald
    case "EXIT":
      return { backgroundColor: "#fee2e2", color: "#991b1b" }; // Rose
    case "PROMOTION":
      return { backgroundColor: "#e0e7ff", color: "#3730a3" }; // Indigo
    case "TRANSFER":
      return { backgroundColor: "#ffedd5", color: "#9a3412" }; // Orange / Amber
    case "INCREMENT":
      return { backgroundColor: "#f3e8ff", color: "#6b21a8" }; // Purple
    case "CONFIRMATION":
      return { backgroundColor: "#e0f2fe", color: "#075985" }; // Sky
    default:
      return { backgroundColor: "#f3f4f6", color: "#374151" }; // Gray
  }
};

export default function StaffMovementReport() {
  const tenant = localStorage.getItem("tenant") || "";
  const token = sessionStorage.getItem("token") || "";

  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      const u = JSON.parse(storedUser);
      setCurrentUserRole(u.role);
      setPermissions(u.permissions || []);
    }
  }, []);

  const isSalaryVisible = React.useMemo(() => {
    const r = currentUserRole?.toLowerCase();
    return permissions.includes("ACCESS_PAYROLL") || permissions.includes("VIEW_PAYROLL") || r === "super_admin" || r === "hr_manager";
  }, [currentUserRole, permissions]);

  // Date Filters (Default to current month range)
  const getInitialDates = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const firstDay = new Date(y, m, 1).toISOString().slice(0, 10);
    const lastDay = new Date(y, m + 1, 0).toISOString().slice(0, 10);
    return { firstDay, lastDay };
  };

  const { firstDay, lastDay } = getInitialDates();
  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(lastDay);
  const [branchId, setBranchId] = useState("");
  const [orgNodeId, setOrgNodeId] = useState("");

  // Metadata Dropdowns
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Data Loading States
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  // Grouped Category Filter States
  const [categoryFilters, setCategoryFilters] = useState({
    newHires: true,
    exits: true,
    transitions: true,
    salaryChanges: true
  });

  // Load Metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      const config = {
        withCredentials: true,
        headers: {
          "X-Tenant": tenant,
          "Authorization": `Bearer ${token}`
        }
      };
      try {
        const [branchRes, nodeRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, config),
          axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, config)
        ]);

        if (Array.isArray(branchRes.data?.data)) setBranches(branchRes.data.data);
        if (Array.isArray(nodeRes.data?.data)) setDepartments(buildHierarchicalList(nodeRes.data.data));
      } catch (err) {
        console.error("Failed to load metadata filters:", err);
      }
    };

    if (tenant && token) {
      fetchMetadata();
    }
  }, [tenant, token]);

  const onSearch = async () => {
    if (!tenant) {
      setError("Missing tenant configuration.");
      return;
    }
    if (!dateFrom || !dateTo) {
      setError("Please select both start and end dates.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await fetchStaffMovement({
        date_from: dateFrom,
        date_to: dateTo,
        branch_id: branchId || undefined,
        org_node_id: orgNodeId || undefined
      });

      if (res.success) {
        setRows(res.rows || []);
      } else {
        throw new Error(res.message || "Failed to load report");
      }
    } catch (e) {
      setError(e?.message || "Failed to load report");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenant) onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  // Filtered Rows list based on Checked Categories
  const filteredRows = rows.filter(r => {
    const type = r.event_type;

    if (type === 'JOINING' || type === 'ONBOARDING' || type === 'REHIRE') {
      return categoryFilters.newHires;
    }

    if (type === 'EXIT' || type === 'RESIGNED' || type === 'TERMINATED' || type === 'CESSATION') {
      return categoryFilters.exits;
    }

    if (type === 'PROMOTION' || type === 'TRANSFER' || type === 'REDESIGNATION' || type === 'CONFIRMATION' || type === 'DEMOTION') {
      return categoryFilters.transitions;
    }

    if (type === 'INCREMENT' || type === 'ANNUAL_INCREMENT' || type === 'DECREMENT') {
      return isSalaryVisible && categoryFilters.salaryChanges;
    }

    return true;
  });

  const onExportExcel = async () => {
    if (!dateFrom || !dateTo) {
      setError("Please select date ranges before exporting.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const url = buildStaffMovementExcelUrl({
        date_from: dateFrom,
        date_to: dateTo,
        branch_id: branchId || undefined,
        org_node_id: orgNodeId || undefined
      });

      const res = await fetch(url, {
        credentials: "include",
        headers: { "X-Tenant": tenant },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      const blob = await res.blob();
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `staff_movements_${dateFrom}_to_${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setError(e?.message || "Excel export failed");
    } finally {
      setLoading(false);
    }
  };

  const formatExcelDetails = (details, type) => {
    if (!details) return "-";
    if (type === 'EXIT') return `Exit Status: ${details.status || ""}`;
    const parts = [];
    if (details.dept_name) parts.push(`Dept: ${details.dept_name}`);
    if (details.branch_name) parts.push(`Branch: ${details.branch_name}`);
    if (details.job_role) parts.push(`Role: ${details.job_role}`);
    if (details.grade) parts.push(`Grade: ${details.grade}`);
    if (isSalaryVisible && details.salary !== undefined && details.salary !== null) {
      parts.push(`Salary: ${details.salary.toLocaleString()}`);
    }
    return parts.join(" | ");
  };

  const onExportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "legal" });

      if (myanmarFontBase64) {
        doc.addFileToVFS("Padauk-Regular.ttf", myanmarFontBase64);
        doc.addFont("Padauk-Regular.ttf", "Padauk", "normal");
        doc.addFont("Padauk-Regular.ttf", "Padauk", "bold");
      }
      const defaultFont = myanmarFontBase64 ? "Padauk" : "helvetica";

      doc.setFont(defaultFont, "bold");
      doc.setFontSize(16);
      doc.setTextColor(30, 41, 59);
      doc.text("Staff Movements & Events Report", 14, 15);

      doc.setFont(defaultFont, "normal");
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Period: ${dateFrom} to ${dateTo} | Generated At: ${new Date().toLocaleDateString()}`, 14, 21);

      const tableHeaders = ["No.", "Date", "Employee ID", "Staff Name", "Event Type", "Previous Details", "New Details", "Remarks"];
      const tableRows = filteredRows.map((r, index) => {
        return [
          index + 1,
          r.event_date,
          r.employee_id,
          r.full_name,
          r.event_type,
          formatExcelDetails(r.prev_details, null),
          formatExcelDetails(r.new_details, r.event_type),
          r.remarks || ""
        ];
      });

      autoTable(doc, {
        startY: 26,
        head: [tableHeaders],
        body: tableRows,
        theme: "striped",
        headStyles: { fillColor: [79, 70, 229], font: defaultFont, fontStyle: "bold" },
        styles: { fontSize: 8, font: defaultFont },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 20 },
          2: { cellWidth: 22 },
          3: { cellWidth: 35 },
          4: { cellWidth: 25 },
          5: { cellWidth: 80 },
          6: { cellWidth: 80 },
          7: { cellWidth: 45 }
        }
      });

      doc.save(`staff_movements_${dateFrom}_to_${dateTo}.pdf`);
    } catch (e) {
      setError(e?.message || "PDF Export failed");
    }
  };
  const renderDetailsDiff = (prev, curr, eventType) => {
    if (eventType === "JOINING" || eventType === "REHIRE") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ fontSize: "10px", color: "#056f46", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.03em" }}>
            {eventType === "REHIRE" ? "Rehire Assignment" : "Initial Assignment"}
          </span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", fontSize: "12px", color: "#334155" }}>
            {curr.dept_name && <div><strong>Dept:</strong> {curr.dept_name}</div>}
            {curr.branch_name && <div><strong>Branch:</strong> {curr.branch_name}</div>}
            {curr.job_role && <div><strong>Role:</strong> {curr.job_role}</div>}
            {curr.grade && <div><strong>Grade:</strong> {curr.grade}</div>}
            {isSalaryVisible && curr.salary && <div><strong>Salary:</strong> {curr.salary.toLocaleString()}</div>}
          </div>
        </div>
      );
    }

    if (eventType === "EXIT") {
      return (
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16 }}>
          <div>
            <span style={{ display: "block", fontSize: "10px", color: "#991b1b", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.03em", marginBottom: 3 }}>Last Active Placement</span>
            {prev && (
              <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: "12px", color: "#475569" }}>
                {prev.dept_name && <div><strong>Dept:</strong> {prev.dept_name}</div>}
                {prev.branch_name && <div><strong>Branch:</strong> {prev.branch_name}</div>}
                {prev.job_role && <div><strong>Role:</strong> {prev.job_role}</div>}
              </div>
            )}
          </div>
          <div>
            <span style={{ display: "block", fontSize: "10px", color: "#991b1b", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.03em", marginBottom: 3 }}>Exit Status</span>
            <div style={{ fontSize: "13px", color: "#ef4444", fontWeight: 700 }}>
              Status: {curr.status || "EXIT"}
            </div>
          </div>
        </div>
      );
    }

    // Career Changes (PROMOTION, TRANSFER, INCREMENT, etc.)
    const changes = [];
    const fields = [
      { key: "dept_name", label: "Dept" },
      { key: "branch_name", label: "Branch" },
      { key: "job_role", label: "Role" },
      { key: "grade", label: "Grade" },
      ...(isSalaryVisible ? [{ key: "salary", label: "Salary", isMonetary: true }] : [])
    ];

    fields.forEach(f => {
      const prevVal = prev?.[f.key];
      const currVal = curr?.[f.key];
      if (prevVal !== currVal && (prevVal !== undefined || currVal !== undefined)) {
        changes.push({
          label: f.label,
          prev: f.isMonetary && prevVal ? prevVal.toLocaleString() : (prevVal || "-"),
          curr: f.isMonetary && currVal ? currVal.toLocaleString() : (currVal || "-")
        });
      }
    });

    if (changes.length === 0) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ fontSize: "10px", color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.03em" }}>Details</span>
          <div style={{ color: "#64748b", fontSize: "12px" }}>Assignment updated (no comparison diff)</div>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ display: "block", fontSize: "10px", color: "#3730a3", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.03em" }}>Career Placement Changes</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {changes.map(ch => (
            <div key={ch.label} style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 700, color: "#475569", minWidth: 60 }}>{ch.label}:</span>
              <span style={{ color: "#94a3b8", textDecoration: "line-through" }}>{ch.prev}</span>
              <span style={{ color: "primary.dark", fontWeight: 900 }}>➜</span>
              <span style={{ color: "#1e293b", fontWeight: 700 }}>{ch.curr}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      <AppHeader />

      <div style={{ padding: "24px 32px", maxWidth: 1450, margin: "0 auto" }}>
        <h2 style={{ fontSize: "24px", fontWeight: 800, color: "#1e293b", marginBottom: 4 }}>
          Staff Movement & Events Report
        </h2>
        <p style={{ color: "#64748b", margin: "0 0 24px 0" }}>
          Monitor employee career changes, onboardings, exits, transfers, and promotions within a specific date range.
        </p>

        {/* Filters and Action Bar */}
        <div style={{
          backgroundColor: "#ffffff",
          padding: "24px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
          marginBottom: 20
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Date From</label>
              <input 
                type="date" 
                value={dateFrom} 
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Date To</label>
              <input 
                type="date" 
                value={dateTo} 
                onChange={(e) => setDateTo(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Branch</label>
              <select 
                value={branchId} 
                onChange={(e) => setBranchId(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
              >
                <option value="">All Branches</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Department</label>
              <select 
                value={orgNodeId} 
                onChange={(e) => setOrgNodeId(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
              >
                <option value="">All Departments</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>
                    {'\u00a0'.repeat((d._depth || 0) * 2)}{d._depth > 0 ? '\u2514 ' : ''}{d.node_name}{d.level_type ? ` (${d.level_type})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            {/* Event Filter Checkboxes */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <span style={{ fontSize: "12px", fontWeight: 800, color: "#475569", alignSelf: "center" }}>Event Categories:</span>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "13px", color: "#334155", cursor: "pointer", userSelect: "none" }}>
                <input 
                  type="checkbox" 
                  checked={categoryFilters.newHires}
                  onChange={() => setCategoryFilters(prev => ({ ...prev, newHires: !prev.newHires }))}
                  style={{ cursor: "pointer" }}
                />
                New Hires & Entries
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "13px", color: "#334155", cursor: "pointer", userSelect: "none" }}>
                <input 
                  type="checkbox" 
                  checked={categoryFilters.exits}
                  onChange={() => setCategoryFilters(prev => ({ ...prev, exits: !prev.exits }))}
                  style={{ cursor: "pointer" }}
                />
                Exits & Cessations
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "13px", color: "#334155", cursor: "pointer", userSelect: "none" }}>
                <input 
                  type="checkbox" 
                  checked={categoryFilters.transitions}
                  onChange={() => setCategoryFilters(prev => ({ ...prev, transitions: !prev.transitions }))}
                  style={{ cursor: "pointer" }}
                />
                Career Transitions
              </label>
              {isSalaryVisible && (
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "13px", color: "#334155", cursor: "pointer", userSelect: "none" }}>
                  <input 
                    type="checkbox" 
                    checked={categoryFilters.salaryChanges}
                    onChange={() => setCategoryFilters(prev => ({ ...prev, salaryChanges: !prev.salaryChanges }))}
                    style={{ cursor: "pointer" }}
                  />
                  Compensation Changes
                </label>
              )}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button 
                onClick={onSearch} 
                disabled={loading}
                style={{
                  backgroundColor: "primary.dark",
                  color: "#ffffff",
                  padding: "8px 18px",
                  borderRadius: 6,
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: loading ? 0.6 : 1
                }}
              >
                Search
              </button>
              <button 
                onClick={onExportExcel} 
                disabled={loading || filteredRows.length === 0}
                style={{
                  backgroundColor: "#10b981",
                  color: "#ffffff",
                  padding: "8px 18px",
                  borderRadius: 6,
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: (loading || filteredRows.length === 0) ? 0.6 : 1
                }}
              >
                Export Excel
              </button>
              <button 
                onClick={onExportPDF} 
                disabled={loading || filteredRows.length === 0}
                style={{
                  backgroundColor: "#e11d48",
                  color: "#ffffff",
                  padding: "8px 18px",
                  borderRadius: 6,
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: (loading || filteredRows.length === 0) ? 0.6 : 1
                }}
              >
                Print PDF
              </button>
            </div>
          </div>
        </div>

        {error && <div style={{ marginBottom: 16, color: "#ef4444", fontSize: "14px", fontWeight: 600 }}>{error}</div>}
        {loading && <div style={{ marginBottom: 16, color: "primary.dark", fontSize: "14px" }}>Loading movements data...</div>}

        {/* Data Grid Table View */}
        <div style={{
          backgroundColor: "#ffffff",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)"
        }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", color: "#334155" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0", width: 60 }}>No.</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0", width: 100 }}>Date</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0", width: 120 }}>Employee ID</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0", width: 180 }}>Staff Name</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0", width: 140 }}>Event Type</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>Movement Details Comparison</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, width: 200 }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r, index) => (
                  <tr key={r.staff_id + "-" + r.event_type + "-" + r.event_date + "-" + index} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "14px 16px", borderRight: "1px solid #e2e8f0", verticalAlign: "top" }}>{index + 1}</td>
                    <td style={{ padding: "14px 16px", borderRight: "1px solid #e2e8f0", verticalAlign: "top", fontWeight: 600 }}>{r.event_date}</td>
                    <td style={{ padding: "14px 16px", borderRight: "1px solid #e2e8f0", verticalAlign: "top" }}>{r.employee_id}</td>
                    <td style={{ padding: "14px 16px", borderRight: "1px solid #e2e8f0", verticalAlign: "top", fontWeight: 700, color: "#1e293b" }}>{r.full_name}</td>
                    <td style={{ padding: "14px 16px", borderRight: "1px solid #e2e8f0", verticalAlign: "top" }}>
                      <span style={{ 
                        display: "inline-block", 
                        padding: "4px 10px", 
                        borderRadius: "100px", 
                        fontSize: "11px", 
                        fontWeight: 800,
                        letterSpacing: "0.02em",
                        ...getBadgeStyles(r.event_type) 
                      }}>
                        {r.event_type}
                      </span>
                    </td>
                    <td style={{ padding: "14px 16px", borderRight: "1px solid #e2e8f0", verticalAlign: "top" }}>
                      {renderDetailsDiff(r.prev_details, r.new_details, r.event_type)}
                    </td>
                    <td style={{ padding: "14px 16px", verticalAlign: "top", color: "#64748b", fontStyle: "italic" }}>
                      {r.remarks || "-"}
                    </td>
                  </tr>
                ))}

                {!loading && filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: "32px", textAlign: "center", color: "#64748b" }}>
                      No staff movement events found in the selected period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
